import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Checkbox, Col, Divider, Modal, Row, Typography } from 'antd';
import { TbArrowDown, TbCube3dSphere } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { resourceMergeService } from '@/services';
import {
    CompactCollapsible,
    DropdownButton,
    MergeIcon,
    WorkspaceResourceDropdown,
    WorkspaceResourceType,
} from '@/shared';
import { getSnapshotPreviewChangeCount, ResourceSnapshotPreview } from '@/shared/components/resource-history';
import {
    MergeableResourceType,
    ResourceHistoryTarget,
    ResourceMergePreview,
    ResourceVersionListItem,
    SetStateMethod,
} from '@/types';

interface SelectableResource {
    editedName?: string;
    id?: string;
    name?: string;
    title?: string;
    type?: WorkspaceResourceType;
}

interface MergeResourceModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    source: ResourceHistoryTarget;
}

const dropdownTypeByResourceType: Record<MergeableResourceType, WorkspaceResourceType> = {
    dashboards: 'Dashboard',
    folders: 'Folder',
    investments: 'Investment',
    lists: 'List',
    memos: 'Memo',
    pipelines: 'Pipeline',
    reports: 'Report',
    tasks: 'Task',
    teams: 'Team',
};

const resourceLabel = (resource?: SelectableResource) =>
    resource?.name ?? resource?.title ?? resource?.editedName ?? 'Untitled';

const MergeResourceModal: FC<MergeResourceModalProps> = ({ open, setOpen, source }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const resourceType = source.resourceType as MergeableResourceType;
    const dropdownType = dropdownTypeByResourceType[resourceType];
    const [sourceResource, setSourceResource] = useState<SelectableResource>({});
    const [targetResource, setTargetResource] = useState<SelectableResource>({});
    const [archiveSource, setArchiveSource] = useState(true);
    const [diffOpen, setDiffOpen] = useState(true);
    const [preview, setPreview] = useState<ResourceMergePreview>();
    const [previewLoading, setPreviewLoading] = useState(false);
    const [mergeLoading, setMergeLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        setSourceResource({
            id: source.resourceId,
            title: source.title ?? 'Untitled',
            type: dropdownType,
        });
        setTargetResource({});
        setPreview(undefined);
        setArchiveSource(true);
        setDiffOpen(true);
    }, [dropdownType, open, source.resourceId, source.title]);

    useEffect(() => {
        const sourceId = sourceResource.id;
        const targetId = targetResource.id;

        if (!open || !sourceId || !targetId || sourceId === targetId) {
            setPreview(undefined);

            return;
        }

        let active = true;
        setPreviewLoading(true);

        resourceMergeService
            .preview({ resourceType, sourceId, targetId })
            .then((nextPreview) => {
                if (active) {
                    setPreview(nextPreview);
                }
            })
            .catch((error: unknown) => {
                captureException(error);
                if (active) {
                    setPreview(undefined);
                    toast.error('Unable to preview this merge.');
                }
            })
            .finally(() => {
                if (active) {
                    setPreviewLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [open, resourceType, sourceResource.id, targetResource.id]);

    const previewVersion = useMemo<ResourceVersionListItem | undefined>(() => {
        if (!preview) {
            return undefined;
        }

        return {
            activityId: 'merge-preview',
            changes: preview.changes,
            checksum: preview.targetChecksum,
            createdAt: new Date().toISOString(),
            id: 'merge-preview',
            locked: false,
            snapshot: preview.mergedSnapshot,
            title: `${resourceLabel(sourceResource)} → ${resourceLabel(targetResource)}`,
        };
    }, [preview, sourceResource, targetResource]);
    const previewChangeCount = useMemo(
        () => (previewVersion ? getSnapshotPreviewChangeCount(resourceType, previewVersion) : 0),
        [previewVersion, resourceType],
    );

    const close = () => {
        if (mergeLoading) {
            return;
        }
        setOpen(false);
    };

    const merge = async () => {
        if (!preview || !sourceResource.id || !targetResource.id) {
            return;
        }

        setMergeLoading(true);

        try {
            const result = await resourceMergeService.execute({
                archiveSource,
                expectedSourceChecksum: preview.sourceChecksum,
                expectedTargetChecksum: preview.targetChecksum,
                resourceType,
                sourceId: sourceResource.id,
                targetId: targetResource.id,
            });
            toast.success('Resources merged', {
                description: result.sourceArchived
                    ? `${resourceLabel(sourceResource)} was merged and archived.`
                    : `${resourceLabel(sourceResource)} was merged into ${resourceLabel(targetResource)}. The source remains available.`,
                icon: <MergeIcon />,
            });
            setTargetResource({});
            setPreview(undefined);
            setArchiveSource(true);
            setDiffOpen(true);
            setOpen(false);
        } catch (error) {
            captureException(error);
            toast.error('The resources could not be merged.', {
                description: 'Refresh the preview and try again. Neither resource was partially updated.',
            });
        } finally {
            setMergeLoading(false);
        }
    };

    const resourceButton = (resource: SelectableResource, placeholder: string) => (
        <DropdownButton block style={{ justifyContent: 'flex-start' }}>
            <Row align='middle' style={{ width: '100%' }} wrap={false}>
                <Col flex='none'>
                    <Avatar
                        icon={<TbCube3dSphere size='0.75rem' style={{ color: token.colorTextTertiary }} />}
                        shape='square'
                        size={16}
                        style={{ marginRight: '0.375rem', verticalAlign: '-1px' }}
                    />
                </Col>
                <Col
                    flex='auto'
                    style={{ minWidth: 0, overflow: 'hidden', textAlign: 'left', textOverflow: 'ellipsis' }}
                >
                    {resource.id ? resourceLabel(resource) : placeholder}
                </Col>
            </Row>
        </DropdownButton>
    );

    return (
        <Modal
            destroyOnClose
            maskClosable={!mergeLoading}
            open={open}
            title='Merge Resources'
            width={720}
            styles={{ body: { maxHeight: 'calc(100vh - 10rem)', overflow: 'hidden' } }}
            onCancel={close}
            footer={
                <Row align='middle' style={{ width: '100%' }}>
                    <Col flex='none'>
                        <Button disabled={mergeLoading} onClick={close}>
                            Cancel
                        </Button>
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <Button
                            disabled={!preview || previewLoading}
                            icon={<MergeIcon />}
                            loading={mergeLoading}
                            type='primary'
                            onClick={merge}
                        >
                            Merge Resources
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Typography.Paragraph style={{ color: token.colorTextSecondary }}>
                Choose the source and surviving resource. The preview shows every property that will change before
                anything is written.
            </Typography.Paragraph>
            <WorkspaceResourceDropdown
                excludeResourceIds={targetResource.id ? [targetResource.id] : []}
                resource={sourceResource}
                resourceTypes={[dropdownType]}
                setResource={setSourceResource}
            >
                {resourceButton(sourceResource, 'Select resource to merge…')}
            </WorkspaceResourceDropdown>
            <Divider>
                <Avatar
                    icon={<TbArrowDown size='75%' style={{ color: token.colorTextTertiary }} />}
                    shape='square'
                    size='small'
                    style={{ border: `1px solid ${token.colorBorder}` }}
                />
            </Divider>
            <WorkspaceResourceDropdown
                excludeResourceIds={sourceResource.id ? [sourceResource.id] : []}
                resource={targetResource}
                resourceTypes={[dropdownType]}
                setResource={setTargetResource}
            >
                {resourceButton(targetResource, 'Select the surviving resource…')}
            </WorkspaceResourceDropdown>
            <Checkbox
                checked={archiveSource}
                style={{ margin: '1rem 0' }}
                onChange={(event) => setArchiveSource(event.target.checked)}
            >
                Archive the source resource after a successful merge (when supported)
            </Checkbox>
            <CompactCollapsible
                bordered
                headerExtra={
                    previewLoading ? (
                        <Typography.Text type='secondary'>Comparing…</Typography.Text>
                    ) : preview ? (
                        <Typography.Text type='secondary'>
                            {previewChangeCount} {previewChangeCount === 1 ? 'change' : 'changes'}
                        </Typography.Text>
                    ) : undefined
                }
                label='Property Diff'
                open={diffOpen}
                contentStyle={{
                    maxHeight: 'clamp(12rem, calc(100vh - 28rem), 36rem)',
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    scrollbarGutter: 'stable',
                }}
                onOpenChange={setDiffOpen}
            >
                {previewVersion ? (
                    <ResourceSnapshotPreview resourceType={resourceType} version={previewVersion} />
                ) : (
                    <Typography.Text type='secondary'>
                        Select two resources to compare their property values.
                    </Typography.Text>
                )}
            </CompactCollapsible>
        </Modal>
    );
};

export default MergeResourceModal;
